/**
 * ViewFilterButton — filter icon with dropdown menu for view-level filtering.
 *
 * Renders a filter-funnel icon in the view header. When clicked, opens a
 * dropdown menu with filter options:
 *   - Search field (free-text filter)
 *   - Tags (clickable chips for tag filtering)
 *   - Show Completed / Show Canceled (checkbox toggles)
 *   - Has Deadline / Overdue Only (checkbox toggles)
 *   - Sort by (radio group: Default, Title, Created, Due Date)
 *
 * The icon shows a dot indicator when any non-default filter is active.
 *
 * Uses the viewFilterStore for status/deadline/sort state and
 * tagFilterStore for tag and text filtering. Both are applied in ViewShell.
 */

import { useCallback, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/app/components/ui/dropdown-menu"
import { cn } from "@/shared/utils/general/cn"
import {
  useViewFilterStore,
  type SortOption,
} from "@/features/views/stores/viewFilterStore"
import {
  useTagFilterStore,
  viewShowsTagFilter,
} from "@/features/tags/tagFilterStore"
import { useViewContext } from "@/app/stores/viewContext"
import {
  trackFilterApplied,
  trackSortChanged,
} from "@/shared/services/analyticsTrackers"
import {
  useAncestorTagStore,
} from "@/features/tags/ancestorTagStore"
import { ObjectType } from "@/db/schema"
import FilterFunnelIcon from "@/shared/icons/pikaicons/stroke/general/filter-funnel.svg?react"
import SolidFilterFunnelIcon from "@/shared/icons/pikaicons/solid/general/filter-funnel.svg?react"

export function ViewFilterButton() {
  const { t } = useTranslation()
  const viewId = useViewContext((s) => s.viewId)

  // ── View filter store (status, deadline, sort) ──
  const isViewFilterActive = useViewFilterStore((s) => s.isFiltering())
  const showCompleted = useViewFilterStore((s) => s.showCompleted)
  const showCanceled = useViewFilterStore((s) => s.showCanceled)
  const hasDueDate = useViewFilterStore((s) => s.hasDueDate)
  const overdueOnly = useViewFilterStore((s) => s.overdueOnly)
  const sort = useViewFilterStore((s) => s.sort)
  const areaId = useViewFilterStore((s) => s.areaId)
  const viewFilterStore = useViewFilterStore

  // ── Tag filter store (tags, text) ──
  const showTags = viewShowsTagFilter(viewId)
  const availableTags = useTagFilterStore((s) => s.availableTags)
  const selectedTags = useTagFilterStore((s) => s.selectedTags)
  const filterText = useTagFilterStore((s) => s.filterText)
  const toggleTag = useTagFilterStore((s) => s.toggleTag)
  const setFilterText = useTagFilterStore((s) => s.setFilterText)
  const tagFilterStore = useTagFilterStore
  const searchRef = useRef<HTMLInputElement>(null)

  const isTagFilterActive = selectedTags.length > 0 || !!filterText
  const isActive = isViewFilterActive || isTagFilterActive

  // ── Area list (derived from ancestorTagStore) ──
  const containerTitles = useAncestorTagStore((s) => s.containerTitles)
  const containerTypes = useAncestorTagStore((s) => s.containerTypes)
  const areas = useMemo(() => {
    const result: Array<{ id: string; title: string }> = []
    for (const [id, type] of Object.entries(containerTypes)) {
      if (type === ObjectType.Area) {
        result.push({ id, title: containerTitles[id] || t("New Area") })
      }
    }
    result.sort((a, b) => a.title.localeCompare(b.title))
    return result
  }, [containerTypes, containerTitles, t])

  const handleSortChange = useCallback(
    (value: string) => {
      viewFilterStore.getState().setSort(value as SortOption)
      trackSortChanged(value as "date" | "priority" | "name" | "created")
    },
    [viewFilterStore],
  )

  const handleReset = useCallback(() => {
    viewFilterStore.getState().reset()
    tagFilterStore.getState().reset()
  }, [viewFilterStore, tagFilterStore])

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Prevent dropdown from closing on typing
      e.stopPropagation()
      if (e.key === "Escape") {
        if (filterText) {
          setFilterText("")
        }
      }
    },
    [filterText, setFilterText],
  )

  const Icon = isActive ? SolidFilterFunnelIcon : FilterFunnelIcon

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "relative flex shrink-0 items-center justify-center rounded-md p-1 transition-colors",
            isActive
              ? "text-primary hover:text-primary/80"
              : "text-muted-foreground/40 hover:text-muted-foreground",
          )}
          aria-label={t("Filter")}
        >
          <Icon className="size-5" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {/* ── Search section ── */}
        <div className="px-2 py-1.5">
          <input
            ref={searchRef}
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={t("Search…")}
            className={cn(
              "h-7 w-full rounded-md border bg-transparent px-2 text-sm outline-none",
              "placeholder:text-muted-foreground/50",
              filterText
                ? "border-primary text-foreground"
                : "border-border text-muted-foreground focus:border-primary",
            )}
          />
        </div>

        {/* ── Tags section ── */}
        {showTags && availableTags.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t("Tags")}</DropdownMenuLabel>
            <div className="flex flex-wrap gap-1 px-2 pb-1.5">
              {availableTags.map((tag) => {
                const selected = selectedTags.includes(tag)
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => {
                      toggleTag(tag)
                      trackFilterApplied("tag")
                    }}
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
                      selected
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-secondary-foreground hover:bg-secondary/80",
                    )}
                  >
                    {tag}
                  </button>
                )
              })}
            </div>
          </>
        )}

        <DropdownMenuSeparator />

        {/* ── Status section ── */}
        <DropdownMenuLabel>{t("Status")}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={showCompleted}
          onCheckedChange={(checked) => {
            viewFilterStore.getState().setShowCompleted(!!checked)
            trackFilterApplied("show_completed")
          }}
        >
          {t("Show Completed")}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={showCanceled}
          onCheckedChange={(checked) => {
            viewFilterStore.getState().setShowCanceled(!!checked)
            trackFilterApplied("show_canceled")
          }}
        >
          {t("Show Canceled")}
        </DropdownMenuCheckboxItem>

        <DropdownMenuSeparator />

        {/* ── Deadline section ── */}
        <DropdownMenuLabel>{t("Deadline")}</DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={hasDueDate === true}
          onCheckedChange={(checked) => {
            viewFilterStore.getState().setHasDueDate(checked ? true : null)
            trackFilterApplied("has_deadline")
          }}
        >
          {t("Has Deadline")}
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={overdueOnly}
          onCheckedChange={(checked) => {
            viewFilterStore.getState().setOverdueOnly(!!checked)
            trackFilterApplied("overdue_only")
          }}
        >
          {t("Overdue Only")}
        </DropdownMenuCheckboxItem>

        <DropdownMenuSeparator />

        {/* ── Area section ── */}
        {areas.length > 0 && (
          <>
            <DropdownMenuLabel>{t("Area")}</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={areaId ?? ""}
              onValueChange={(value) => {
                viewFilterStore.getState().setAreaId(value || null)
                trackFilterApplied("area")
              }}
            >
              <DropdownMenuRadioItem value="">
                {t("All Areas")}
              </DropdownMenuRadioItem>
              {areas.map((area) => (
                <DropdownMenuRadioItem key={area.id} value={area.id}>
                  {area.title}
                </DropdownMenuRadioItem>
              ))}
              <DropdownMenuRadioItem value="__no-area__">
                {t("No Area")}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}

        {/* ── Sort section ── */}
        <DropdownMenuLabel>{t("Sort By")}</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={sort} onValueChange={handleSortChange}>
          <DropdownMenuRadioItem value="default">
            {t("Default")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="title-asc">
            {t("Title A–Z")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="title-desc">
            {t("Title Z–A")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="created-newest">
            {t("Newest First")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="created-oldest">
            {t("Oldest First")}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="due-date">
            {t("Due Date")}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>

        {/* ── Reset ── */}
        {isActive && (
          <>
            <DropdownMenuSeparator />
            <button
              type="button"
              onClick={handleReset}
              className="w-full rounded-lg px-2 py-1.5 text-left text-sm text-destructive hover:bg-destructive/10"
            >
              {t("Clear All Filters")}
            </button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
